import { h, Component } from 'preact';
import Store from 'store/store';
import Filter from 'bad-words';
import 'store/bolt/quizPhotobooth';

import { getMainPage } from 'utils/bolt';
import { commaSeparatedList, joinClasses } from 'utils/utils';
import parseQueryString from 'utils/parseQueryString';
import { init } from 'utils/poster';

import Transition from 'components/core/transition/transition';
import Intro from 'components/bolt/quizPhotobooth/intro';
import Photobooth from 'components/bolt/quizPhotobooth/photobooth';
import BackgroundSelector from 'components/bolt/quizPhotobooth/backgroundSelector';
import QuoteSelector from 'components/bolt/quizPhotobooth/quoteSelector';
import Shareable from 'components/bolt/quizPhotobooth/shareable';

import s from 'components/bolt/quizPhotobooth/quizPhotobooth.sss';

const durationPerTransition = {
	fade: 500,
	'slide-vertical': 2000,
};

const cssURL = e => !!e && `url(${e})`;

const getDefaultSideContent = (_, side, sides) => ({
	background: 0,
	photo: null,
	photoTransform: typeof window !== 'undefined' && new DOMMatrix().translate(sides.length === 2 ? (side * 2 - 1) * -200 : 0, sides.length === 2 ? (side - .5) * 200 : 0),
});

export default class QuizPhotobooth extends Component {
	constructor(props) {
		super(props);
		this.updateCardData = this.updateCardData.bind(this);
		this.advanceStep = this.advanceStep.bind(this);
		this.backStep = this.backStep.bind(this);
		this.backToIntro = this.backToIntro.bind(this);
		this.setInput = this.setInput.bind(this);
		this.endRevealAnim = this.endRevealAnim.bind(this);
		this.setDualMode = this.setDualMode.bind(this);
		this.selectSide = this.selectSide.bind(this);
		this.handleLogoClick = this.handleLogoClick.bind(this);
		this.transition = props?.data?.transition || 'slide-vertical';
		this.transitionDuration = durationPerTransition[this.transition] || 1000;

		let sequence = commaSeparatedList(props?.data?.sequence);
		let step = sequence[0];
		let inputs = {};
		let defaultCardData = {
			name: '',
			quote: '',
			sides: [...Array(2)].map(getDefaultSideContent),
		};
		let selectedSide = null;
		// Easier testing
		if (__IS_LOCAL__ || __IS_DEMO__) {
			let testStep = (parseQueryString().step) || props?.data?.testStep;
			if (testStep) {
				step = testStep;
				if (['browserErrorScreen', 'cameraDeniedScreen', 'deviceErrorScreen'].includes(step)) {
					step = 'photobooth';
				}
				if (!sequence.includes(step)) console.warn(`test step ${step} is not part of the expected steps (${sequence.join(', ')})`);
				if (sequence.indexOf(step) > sequence.indexOf('intro')) {
					inputs = { name: 'Name' };
					defaultCardData.name = inputs.name;
				}
				if (sequence.indexOf(step) > sequence.indexOf('photobooth')) {
					selectedSide = 1;
					let placeholder = new Image();
					placeholder.crossOrigin = 'anonymous';
					placeholder.src = 'https://corsproxy.io/?https://www.freeiconspng.com/uploads/man-face-png-6.png';
					placeholder.onload = () => this.setState({ cardData: { ...this.state.cardData } });
					defaultCardData.sides[selectedSide].photo = placeholder;
					defaultCardData.sides[selectedSide].photoTransform.scaleSelf(1.3, 1.3, 0).translateSelf(0, 100);
					defaultCardData.sides[0].photo = placeholder;
					defaultCardData.sides[0].photoTransform.rotateSelf(0, 0, 20).translateSelf(-20, 100);
				}
				if (sequence.indexOf(step) > sequence.indexOf('background')) {
					defaultCardData.sides.forEach((side, index) => (side.background = index));
				}
				if (sequence.indexOf(step) > sequence.indexOf('quote')) {
					defaultCardData.quote = 'We\'re off to see the\nWicked movie!';
				}
			}
		}

		this.state = {
			cardData: defaultCardData,
			selectedSide,
			sequence,
			inputs,
			step,
			stepDirection: 'forward',
			revealAnimEnded: false,
			isDualMode: false,
		};

		if (typeof window !== 'undefined') {
			init().then(() => {
				// Forces redraw on child components if needed
				this.setState({ cardData: { ...this.state.cardData } });
			});
		}
	}

	componentDidMount() {
		this.badWords = new Filter();
		this.badWords.addWords(...commaSeparatedList(this.props?.data?.intro?.profanities));
		const sequence = this.props?.data?.sequence;
		const showResultScreen = this.props?.data?.showResultScreen;
		Store.get().quizPhotobooth.set({ sequence: sequence, step: this.state.step, question: 0, showResultScreen: showResultScreen });
		Store.on('QUIZPHOTOBOOTH_BACK', this.backStep);
		Store.on('QUIZPHOTOBOOTH_BACK_TO_INTRO', this.backToIntro);
	}

	componentWillUnmount() {
		Store.off('QUIZPHOTOBOOTH_BACK', this.backStep);
		Store.off('QUIZPHOTOBOOTH_BACK_TO_INTRO', this.backToIntro);
		clearTimeout(this.nextQuestionTimer);
	}

	advanceStep(additionalData) {
		const sequence = this.state.sequence;
		const currentStep = this.state.step;
		let nextStep = sequence.indexOf(currentStep) + 1;
		if (currentStep === 'background') {
			const nextSide = this.state.cardData.sides.findIndex(side => !side.photo);
			if (nextSide > -1) {
				this.setState({ selectedSide: nextSide });
				nextStep = sequence.indexOf('photobooth');
			}
		}
		if (nextStep > sequence.length) return;
		let trackViewLabel = sequence[nextStep];
		const allowChoosingShareableFrame = this.props.data?.shareable?.allowChoosingFrame;
		if (sequence[nextStep] === 'shareable' && allowChoosingShareableFrame) {
			trackViewLabel = 'choose-shareable';
		}
		Store.emit('TRACK_QUIZPHOTOBOOTH_VIEW', trackViewLabel);
		this.setState({
			step: sequence[nextStep],
			stepDirection: 'forward',
			// add any additional required data to this.state from the data variable
			...additionalData
		});
		Store.get().quizPhotobooth.set({ step: sequence[nextStep] });
	}

	backStep(clearedData) {
		const sequence = this.state.sequence;
		const currentStep = this.state.step;
		let previousStep = sequence.indexOf(currentStep) - 1;
		if (currentStep === 'photobooth') {
			let otherSide = this.state.cardData.sides.findIndex(side => side.photo);
			if (otherSide > -1) {
				this.setState({ selectedSide: otherSide });
				previousStep = sequence.indexOf('background');
			}
		}
		if (previousStep < 0) return;
		if (sequence[previousStep] === 'intro') {
			Store.emit('QUIZPHOTOBOOTH_SHAREABLE_RESET');
		}
		Store.emit('TRACK_QUIZPHOTOBOOTH_VIEW', sequence[previousStep]);
		this.setState({
			step: sequence[previousStep],
			stepDirection: 'back',
			...clearedData
		});
		Store.get().quizPhotobooth.set({ step: sequence[previousStep] });
		Store.emit('CUSTOM_EVENT', {
			category: 'camera',
			action: 'camera-back-click',
			label: ''
		});
	}

	backToIntro() {
		this.setState({
			step: 'intro',
			stepDirection: 'back',
		});
		Store.get().quizPhotobooth.set({ step: 'intro' });
		Store.emit('TRACK_QUIZPHOTOBOOTH_REDO_CLICK');
	}

	updateCardData(data) {
		this.setState({ cardData: data });
	}

	setInput(input) {
		const newState = {
			inputs: { ...this.state.inputs, ...input }
		};
		if (input?.name) {
			newState.cardData = { ...this.state.cardData, name: input.name };
		}
		this.setState(newState);
	}

	endRevealAnim() {
		this.setState({ revealAnimEnded: true });
	}

	handleLogoClick() {
		Store.emit('TRACK_QUIZPHOTOBOOTH_LOGO_CLICK');
		Store.emit('SET_ACTIVE_PAGE', getMainPage());
	}

	setDualMode(isDualMode) {
		this.setState({
			cardData: { quote: '', sides: [...Array(isDualMode ? 2 : 1)].map(getDefaultSideContent) },
			selectedSide: isDualMode ? null : 0,
		});
	}

	selectSide(side) {
		this.setState({ selectedSide: side });
	}

	renderIntro() {
		const props = this.props;
		return (
			<Intro
				id="quizPhotobooth-intro"
				intro={props.data?.intro}
				assets={props.data?.assets}
				appData={props.appData}
				advanceStep={this.advanceStep}
				setInput={this.setInput}
				isDesktopWidth={props.client?.isDesktopWidth}
				badWords={this.badWords?.list}
				inputValues={this.state.inputs}
				transition={this.transition}
				stepDirection={this.state.stepDirection}
				setDualMode={this.setDualMode}
				cardData={this.state.cardData}
				creativeSpace={this.props.creativeSpace}
			/>
		);
	}

	renderPhotobooth() {
		const props = this.props;
		const client = props.client || {};
		return (
			<Photobooth
				id="quizPhotobooth-photobooth"
				photobooth={props.data?.photobooth}
				assets={props.data?.assets}
				appData={props.appData}
				advanceStep={this.advanceStep}
				backStep={this.backStep}
				backToIntro={this.backToIntro}
				revealAnimEnded={this.state.revealAnimEnded}
				resultData={props.data?.results || {}}
				isDesktopDevice={client.device === 'desktop'}
				deviceWidthSize={client.deviceWidthSize}
				isDesktopWidth={client.isDesktopWidth}
				isIOS={client.isIOS}
				allowedBrowsers={props.data?.allowedBrowsers}
				noQuiz={!this.state.sequence.includes('quiz')}
				transition={this.transition}
				inputs={this.state.inputs}
				testStep={props.data?.testStep}
				stepDirection={this.state.stepDirection}
				cardData={this.state.cardData}
				updateCardData={this.updateCardData}
				selectedSide={this.state.selectedSide}
				selectSide={this.selectSide}
				isActive
			/>
		);
	}

	renderShareable() {
		const props = this.props;
		const client = props.client || {};
		return (
			<Shareable
				id="quizPhotobooth-shareable"
				shareable={props.data?.shareable}
				assets={props.data?.assets}
				appData={props.appData}
				backStep={this.backStep}
				backToIntro={this.backToIntro}
				hasQuiz={this.state.sequence?.includes('quiz')}
				isDesktopDevice={client.device === 'desktop'}
				isDesktopWidth={client.isDesktopWidth}
				client={client}
				isIOS={client.isIOS}
				inputs={this.state.inputs}
				resultData={props.data?.results || {}}
				transitionStyle={this.transition}
				stepDirection={this.state.stepDirection}
				creativeSpace={this.props.creativeSpace}
				cardData={this.state.cardData}
				showFinalShare
			/>
		);
	}

	renderBackgroundSelector() {
		const props = this.props;
		const client = props.client || {};
		return (
			<BackgroundSelector
				id="quizPhotobooth-background"
				appData={props.appData}
				advanceStep={this.advanceStep}
				backStep={this.backStep}
				client={client}
				stepDirection={this.state.stepDirection}
				creativeSpace={this.props.creativeSpace}
				cardData={this.state.cardData}
				selectedSide={this.state.selectedSide}
				updateCardData={this.updateCardData}
			/>
		);
	}
	renderQuoteSelector() {
		const props = this.props;
		const client = props.client || {};
		return (
			<QuoteSelector
				id="quizPhotobooth-quote"
				appData={props.appData}
				quotesSolo={Object.values(props.data?.quotesSolo || {})}
				quotesDual={Object.values(props.data?.quotesDual || {})}
				advanceStep={this.advanceStep}
				backStep={this.backStep}
				client={client}
				stepDirection={this.state.stepDirection}
				creativeSpace={this.props.creativeSpace}
				cardData={this.state.cardData}
				selectedSide={this.state.selectedSide}
				updateCardData={this.updateCardData}
			/>
		);
	}

	renderContent() {
		return {
			intro: this.renderIntro,
			// quiz: this.renderQuiz,
			photobooth: this.renderPhotobooth,
			background: this.renderBackgroundSelector,
			quote: this.renderQuoteSelector,
			shareable: this.renderShareable,
		}[this.state.step]?.call(this);
	}

	render(props, state) {
		const assets = props?.data?.assets;

		const containerStyle = [
			`--transition-duration: ${this.transitionDuration}ms`,
			`--background-mobile: ${cssURL(assets.backgroundMobile)}`,
			`--background-desktop: ${cssURL(assets.backgroundDesktop)}`,
		].filter(e => e).join(';');

		return (
			<div class={joinClasses(s.container, this.props.creativeSpace && s.creativeSpace)} style={containerStyle}>
				{!!assets.topImage && (
					<button class={s.ttHeader} onClick={this.handleLogoClick}>
						<img src={assets.topImage} alt={assets.topImageAltText} />
					</button>
				)}

				<img src={assets.titleImage} class={s.titleImage} alt={assets.titleImageAltText && assets.titleImageAltText} width="691" height="72" />
				<Transition
					duration={this.transitionDuration}
					// duration={1000000}
					class={joinClasses(
						s.transitionWrapper,
						s[`transition-${this.transition}`],
						s[`transition-direction-${state.stepDirection}`],
						this.props.creativeSpace && s.creativeSpace
					)}
					baseClass={s.transitionElement}
					enteringClass={s.transitionEntering}
					visibleClass={s.transitionVisible}
					leavingClass={s.transitionLeaving}
					emptyClass={s.transitionEmpty}
					noInitialTransition
				>
					{this.renderContent()}
				</Transition>
			</div>
		);
	}
}